import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { BaseIndicatorProps } from "../../lib/interface/Indicator.interface";
import { TcTrendDirection, TcTrendSnapshot } from "@lacon/ws";
import {
  createSeriesMarkers,
  ISeriesApi,
  LineSeries,
  Time,
  ISeriesMarkersPluginApi,
} from "lightweight-charts";
import { CandleDto } from "../../lib/interface/CandleDto.interface";

/**
 * Props specific to TcTrend indicator (placeholder)
 */
export interface TcTrendProps extends BaseIndicatorProps {
  trendSnapshot: TcTrendSnapshot | null;
  trendChanges?: { timestamp: number; trend: TcTrendDirection }[];
  formatTime?: (timestamp: number) => number;
  candles?: CandleDto[];
}

/**
 * TcTrend Indicator Component (Placeholder)
 * Currently not implemented - placeholder for future development
 */
export const TcTrendIndicator: React.FC<TcTrendProps> = ({
  isEnabled,
  chartInstance,
  candleSeries,
  trendSnapshot,
  trendChanges = [],
  formatTime,
  candles = [],
}) => {
  // Manage native series markers for trend changes
  const seriesMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const emaRef = useRef<ISeriesApi<"Line"> | null>(null);

  useEffect(() => {
    if (!chartInstance || !candleSeries) return;
    if (!seriesMarkersRef.current) {
      try {
        seriesMarkersRef.current = createSeriesMarkers(candleSeries, []);
      } catch {
        // silently ignore if markers API unavailable
        return;
      }
    }
  }, [chartInstance, candleSeries]);

  // Utility: compute SMMA like backend
  const computeSmma = (values: number[], period: number) => {
    if (values.length === 0) return [];
    const smma = new Array<number>(values.length);
    smma[0] = values[0];
    const k = 1 / period;
    for (let i = 1; i < values.length; i++) {
      smma[i] = smma[i - 1] + k * (values[i] - smma[i - 1]);
    }
    return smma;
  };

  // Compute TcTrend per bar from candles
  const computeTrendSeries = useCallback((closes: number[]) => {
    if (closes.length === 0) return [];
    const v1 = computeSmma(closes, 15);
    const m1 = computeSmma(closes, 19);
    const m2 = computeSmma(closes, 25);
    const v2 = computeSmma(closes, 29);
    const res = new Array<TcTrendDirection | undefined>(closes.length);
    for (let i = 0; i < closes.length; i++) {
      const p2 =
        v1[i] < m1[i] !== v1[i] < v2[i] || m2[i] < v2[i] !== v1[i] < v2[i];
      const p3 = !p2 && v1[i] < v2[i];
      const p1 = !p2 && !p3;
      res[i] = p1
        ? TcTrendDirection.GOLD
        : p2
        ? TcTrendDirection.SILVER
        : TcTrendDirection.BLUE;
    }
    return res;
  }, []);

  // Create/remove single SMMA(21) line series on enable/disable and chart changes
  useEffect(() => {
    if (!chartInstance) {
      return;
    }

    if (!isEnabled) {
      if (emaRef.current) {
        try {
          chartInstance.removeSeries(emaRef.current);
        } catch {}
        emaRef.current = null;
      }
      if (seriesMarkersRef.current) {
        try {
          seriesMarkersRef.current.setMarkers([]);
        } catch {}
      }
      return;
    }

    // Enabled: ensure a line series exists
    if (!emaRef.current) {
      try {
        emaRef.current = chartInstance.addSeries(LineSeries, {
          color: "#a9b4c2",
          lineWidth: 2,
          lineStyle: 0,
          priceLineVisible: false,
          lastValueVisible: false,
        });
      } catch {}
    }

    // Cleanup on chart change/unmount
    return () => {
      if (emaRef.current) {
        try {
          chartInstance.removeSeries(emaRef.current);
        } catch {}
        emaRef.current = null;
      }
    };
  }, [chartInstance, isEnabled]);

  // Update SMMA(21) line and markers (per-point color via LineData.color)
  useEffect(() => {
    if (!chartInstance || !isEnabled) {
      try {
        emaRef.current?.setData([]);
      } catch {}
      try {
        seriesMarkersRef.current?.setMarkers([]);
      } catch {}
      return;
    }
    if (!emaRef.current) return;
    if (!candles || candles.length === 0) {
      try {
        emaRef.current.setData([]);
      } catch {}
      return;
    }

    // Build SMMA(21) values
    const sorted = candles.slice().sort((a, b) => a.timestamp - b.timestamp);
    const closes = sorted.map((c) => Number(c.close));
    const times = sorted.map((c) =>
      formatTime
        ? (formatTime(c.timestamp) as Time)
        : (Math.floor(c.timestamp / 1000) as Time)
    );
    const smma = computeSmma(closes, 21);
    const trendAt = computeTrendSeries(closes);
    const LONGEST_PERIOD = 29;

    const colored = smma.map((v, i) => {
      const trend = i < LONGEST_PERIOD ? TcTrendDirection.SILVER : trendAt[i];
      const color =
        trend === TcTrendDirection.BLUE
          ? "#3b82f6"
          : trend === TcTrendDirection.GOLD
          ? "#ffd700"
          : "#a9b4c2"; // silver / neutral
      return { time: times[i], value: v, color } as {
        time: Time;
        value: number;
        color: string;
      };
    });
    const lastCandleTime =
      times.length > 0 ? times[times.length - 1] : undefined;

    try {
      emaRef.current.setData(colored);
    } catch {}
    // Also update markers for all changes across the chart
    if (seriesMarkersRef.current && formatTime) {
      const markerCandidates = trendChanges.slice(-LONGEST_PERIOD);
      const markers = markerCandidates.map((tc) => {
        const time = formatTime(tc.timestamp) as Time;
        const hideLabel =
          lastCandleTime !== undefined && time === lastCandleTime;
        return {
          time,
          position: "aboveBar" as const,
          shape: "circle" as const,
          color:
            tc.trend === TcTrendDirection.GOLD
              ? "#eab308"
              : tc.trend === TcTrendDirection.SILVER
                ? "#d4d4d8"
                : tc.trend === TcTrendDirection.BLUE
                  ? "#3b82f6"
                  : "#6b7280",
          ...(hideLabel ? {} : { text: tc.trend }),
        };
      });
      try {
        seriesMarkersRef.current.setMarkers(markers);
      } catch {}
    }
  }, [
    chartInstance,
    isEnabled,
    candles,
    formatTime,
    computeTrendSeries,
    trendChanges,
  ]);

  // No fallback color application; color is per-point on a single series

  // retain cleanup for markers on unmount

  useEffect(() => {
    return () => {
      if (seriesMarkersRef.current) {
        try {
          seriesMarkersRef.current.setMarkers([]);
        } catch {}
      }
    };
  }, []);

  const { bg, text } = useMemo(() => {
    if (!trendSnapshot)
      return { bg: "bg-slate-600/80", text: "UNKNOWN" } as const;
    switch (trendSnapshot.trend) {
      case TcTrendDirection.BLUE:
        return { bg: "bg-blue-600/80", text: "BLUE" };
      case TcTrendDirection.GOLD:
        return { bg: "bg-yellow-400/80", text: "GOLD" };
      case TcTrendDirection.SILVER:
        return { bg: "bg-gray-400/80", text: "SILVER" };
      default:
        return { bg: "bg-slate-400/80", text: "UNKNOWN" };
    }
  }, [trendSnapshot]);

  const prev = useMemo(() => {
    if (!trendSnapshot) return "?";
    switch (trendSnapshot.trendPrevious) {
      case TcTrendDirection.BLUE:
        return "BLUE";
      case TcTrendDirection.GOLD:
        return "GOLD";
      case TcTrendDirection.SILVER:
        return "SILVER";
      default:
        return "?";
    }
  }, [trendSnapshot]);

  const updated = useMemo(() => {
    try {
      const ts = trendSnapshot?.updatedAt;
      if (typeof ts !== "number" || !isFinite(ts)) {
        return trendSnapshot ? String(trendSnapshot.updatedAt ?? "") : "";
      }
      const d = ts > 1000000000000 ? new Date(ts) : new Date(ts * 1000);
      return d.toLocaleTimeString();
    } catch {
      return trendSnapshot ? String(trendSnapshot.updatedAt) : "";
    }
  }, [trendSnapshot]);

  return (
    <>
      {isEnabled && trendSnapshot && (
        <div className="absolute top-3 left-3 z-30 select-none">
          <div
            className={`${bg} text-white px-3 py-1 rounded-md shadow-md border border-white/10`}
            title={`Prev: ${prev} • Updated: ${updated}`}
          >
            <div className="flex items-center gap-2 text-sm font-semibold">
              <span>TcTrend</span>
              <span className="opacity-80">→</span>
              <span>{trendSnapshot.trend ?? TcTrendDirection.UNKNOWN}</span>
              <span className="text-xs opacity-70">{text}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
